import React from 'react';
import { useParams } from 'react-router-dom';
import { Link } from '@/components/LocaleLink';
import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import useScrollReveal from '@/hooks/useScrollReveal';
import TheProblemWithEcommerce from './blog/TheProblemWithEcommerce';
import { useLanguage } from '@/contexts/language/LanguageContext';
import { blogProblemWithEcommerceCopy } from '@/contexts/language/translations/pages/blogProblemWithEcommerce';
import { caseStudyChromeCopy } from '@/contexts/language/translations/pages/caseStudyChrome';

// Per-post copy (EN + SK) lives in src/contexts/language/translations/pages/blog*.ts
const BLOG_POSTS: Record<string, {
  copy: typeof blogProblemWithEcommerceCopy;
  dateTime: string;
  Body: React.ComponentType;
}> = {
  'the-problem-with-ecommerce': {
    copy: blogProblemWithEcommerceCopy,
    dateTime: '2026-10',
    Body: TheProblemWithEcommerce,
  },
};

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const entry = slug ? BLOG_POSTS[slug] : undefined;
  useScrollReveal();
  const { language } = useLanguage();
  const ui = caseStudyChromeCopy[language];

  if (!entry) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navigation />
        <main className="flex-1 pt-36 pb-24">
          <div className="container-custom max-w-3xl mx-auto">
            <h1 className="text-3xl font-bold text-[#2A1E47] mb-4">{language === 'sk' ? 'Článok sa nenašiel' : 'Article not found'}</h1>
            <Link to="/content" className="text-[#FF6B35] font-semibold">{ui.backToContent}</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const { Body, dateTime } = entry;
  const post = entry.copy[language].meta;

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title={post.seoTitle}
        description={post.seoDescription}
        canonical={`/blog/${slug}`}
        ogType="article"
      />
      <Navigation />
      <main className="flex-1">

        {/* Hero */}
        <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden bg-[#f8f8fa]">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#E0E7FF] rounded-full filter blur-3xl opacity-30" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#FFF7EC] rounded-full filter blur-3xl opacity-60" />
          <div className="container-custom relative z-10">
            <div className="max-w-4xl mx-auto reveal">
              <div className="flex items-center gap-2 text-[#2A1E47]/40 text-sm mb-8">
                <Link to="/content" className="hover:text-[#2A1E47]/70 transition-colors">{ui.breadcrumbContent}</Link>
                <span>/</span>
                <span className="text-[#2A1E47]/60">{ui.blog}</span>
              </div>

              <div className="flex items-center gap-3 mb-6">
                <span className="text-xs font-bold uppercase tracking-widest bg-[#FFF7EC] text-[#FF6B35] px-3 py-1 rounded-full">
                  {ui.blog}
                </span>
                <span className="text-xs text-[#2A1E47]/40 uppercase tracking-widest font-semibold">
                  {post.category}
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#2A1E47] mb-6 leading-[1.08]">
                {post.titleAccent && post.title.includes(post.titleAccent) ? (
                  <>
                    {post.title.split(post.titleAccent)[0]}
                    <span className="text-[#FF6B35]">{post.titleAccent}</span>
                    {post.title.split(post.titleAccent)[1]}
                  </>
                ) : post.title}
              </h1>

              <p className="text-lg md:text-xl text-[#2A1E47]/70 leading-relaxed max-w-3xl">
                {post.subtitle}
              </p>

              <div className="flex flex-wrap items-center gap-4 text-sm text-[#2A1E47]/50 mt-8">
                <span className="font-semibold text-[#2A1E47]/80">{post.author}</span>
                <span>·</span>
                <time dateTime={dateTime}>{post.date}</time>
                <span>·</span>
                <span>{post.readTime}</span>
              </div>
            </div>
          </div>
        </section>

        <Body />

        {/* CTA / Back */}
        <section className="py-16 bg-[#FAFAFA]">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto text-center mb-8">
              <p className="text-xl md:text-2xl font-bold text-[#2A1E47]">
                {ui.closingLine}
              </p>
            </div>
            <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
              <Link
                to="/content"
                className="flex items-center gap-2 text-sm font-semibold text-[#2A1E47]/60 hover:text-[#2A1E47] transition-colors"
              >
                {ui.backToContent}
              </Link>
              <Link
                to="/tell-me-more"
                className="inline-flex items-center justify-center px-8 py-3 rounded-lg bg-[#FF6B35] text-white font-medium hover:bg-[#FF6B35]/90 transition-colors"
              >
                {post.cta}
              </Link>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;
